import { Text, View } from "@react-pdf/renderer";
import { styles } from "../../ReactPdfStyles";
import { formatDate, formatResponsiblePerson } from "../utils";
import {
	formatDateDdMmmYyyy,
	formatDateTime,
} from "../../../../utils/date_formatter";

type AuditFindingsSectionTypes = {
	findings: any[];
};

export function AuditFindingsSection({ findings }: AuditFindingsSectionTypes) {
	const riskColumns = [
		{ key: "responsible", label: "Responsible" },
		{ key: "completed_on", label: "Completed on" },
		{ key: "what_was_done", label: "What was done" },
	];

	const columnKeys = [
		{ key: "finding_number", label: "Finding no." },
		{ key: "finding_type", label: "Finding type" },
		{ key: "findings_description", label: "Description" },
		{ key: "recommended_actions", label: "Recommended\nactions" },
		{ key: "remarks", label: "Remarks", columns: riskColumns },
	];

	return (
		<View style={[styles.section, styles.tableFontSize]}>
			<Text style={styles.tableHeader}>Findings</Text>

			{/* Header Row */}
			<View style={[styles.row, { marginTop: 16 }]}>
				{columnKeys.map((column) => (
					<View
						style={[
							{ fontWeight: "bold", backgroundColor: "#CDCDCD" },
							styles.valueCell,
							...(column.key === columnKeys[columnKeys.length - 1].key
								? [styles.borderLeft, styles.borderRight]
								: [styles.borderLeft]),
						]}
						key={column.key}
					>
						<Text>{column.label}</Text>
					</View>
				))}
			</View>

			{/* Data Rows */}
			{findings.map((finding, idx) => {
				// Build Risk cell (multiline)
				const responsibleDisplay = formatResponsiblePerson(
					finding?.responsible,
					finding?.responsible_name
				);

				const whatWasDoneLabel = finding.what_was_done ? "What was done:" : "";
				const whatWasDoneValue = finding.what_was_done || "";

				const deadline = new Date(finding?.deadline);
				const completedOn = new Date(finding?.completed_on);

				// Strip time (set to 00:00:00)
				deadline.setHours(0, 0, 0, 0);
				completedOn.setHours(0, 0, 0, 0);

				const isOverdue = deadline < completedOn;

				return (
					<View style={styles.row} key={idx}>
						{columnKeys.map((column) => {
							// Normal columns
							if (column.key !== "remarks") {
								let value = finding[column.key] ?? "";
								if (column.key === "completed_on") value = formatDate(value);

								return (
									<View
										key={column.key}
										style={[
											styles.valueCell,
											...(column.key === columnKeys[columnKeys.length - 1].key
												? [styles.borderLeft, styles.borderRight]
												: [styles.borderLeft]),
										]}
									>
										<Text>{String(value)}</Text>
									</View>
								);
							}

							// RISK COLUMN (special stacked formatting)
							return (
								<View
									key={column.key}
									style={[
										styles.valueCell,
										styles.borderLeft,
										styles.borderRight,
										{ flexDirection: "column", gap: 4, lineHeight: 0.8 },
									]}
								>
									{responsibleDisplay && (
										<View style={{ marginBottom: 8 }}>
											<Text
												style={{
													fontWeight: "bold",
												}}
												wrap={false}
											>
												Person Responsible:
											</Text>

											<Text>{responsibleDisplay}</Text>
										</View>
									)}

									<View style={{ marginBottom: 8 }}>
										<Text style={{ fontWeight: "bold" }}>Deadline:</Text>
										<Text style={isOverdue ? { color: "red" } : {}}>
											{formatDateDdMmmYyyy(finding.deadline ?? "")}
										</Text>
									</View>

									{finding.completed_on && (
										<View style={{ marginBottom: 8 }}>
											<Text style={{ fontWeight: "bold" }}>Completed by: </Text>
											{isOverdue && (
												<Text style={{ color: "red" }}>[Overdue]</Text>
											)}
											<Text>
												{responsibleDisplay} on{" "}
												{formatDateTime(finding.completed_on)}
											</Text>
										</View>
									)}
									{!finding.completed_on && (
										<View style={{ marginBottom: 8 }}>
											<Text style={{ fontStyle: "italic" }}>
												Pending completion
											</Text>
										</View>
									)}

									{/* What was done section */}
									<Text style={{ fontWeight: "bold" }}>{whatWasDoneLabel}</Text>
									<Text>{whatWasDoneValue}</Text>
								</View>
							);
						})}
					</View>
				);
			})}
		</View>
	);
}

export default AuditFindingsSection;
